import React, { useContext, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiArrowRight, FiCheck, FiLock, FiStar, FiTrendingUp, FiX } from "react-icons/fi";
import { FREE_TOOL_LIMIT, TOOL_LIMIT_EVENT, TOOL_USAGE_EVENT, TOOL_USAGE_KEY, getToolUsage } from "./toolUsage";
import { GlobalContext } from "../Context/GlobalState";
import "./ToolUsagePrompt.css";

export default function ToolUsagePrompt() {
  const [open, setOpen] = useState(false);
  const [creditsExpanded, setCreditsExpanded] = useState(false);
  const [flyingCredits, setFlyingCredits] = useState([]);
  const { userData } = useContext(GlobalContext);
  const [used, setUsed] = useState(getToolUsage);
  const remaining = Math.max(0, FREE_TOOL_LIMIT - used);
  const [navbarBottom, setNavbarBottom] = useState(80);
  useEffect(() => {
    const navbar = document.querySelector(".sticky-navbar-wrapper");
    const updatePosition = () => {
      const currentNavbar = document.querySelector(".sticky-navbar-wrapper");
      setNavbarBottom(Math.max(0, currentNavbar?.getBoundingClientRect().bottom || 0) + 12);
    };
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePosition) : null;
    if (navbar) observer?.observe(navbar);
    updatePosition();
    window.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", updatePosition);
    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, []);
  useEffect(() => {
    const update = () => setUsed(getToolUsage());
    const onCreditUsed = () => {
      update();
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        const id = getToolUsage();
        setFlyingCredits(credits => [...credits, id]);
      }
    };
    const syncStorage = (event) => {
      if (event.key === TOOL_USAGE_KEY || event.key === null) update();
    };
    window.addEventListener(TOOL_USAGE_EVENT, onCreditUsed);
    window.addEventListener("storage", syncStorage);
    update();
    return () => {
      window.removeEventListener(TOOL_USAGE_EVENT, onCreditUsed);
      window.removeEventListener("storage", syncStorage);
    };
  }, []);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(TOOL_LIMIT_EVENT, show);
    return () => window.removeEventListener(TOOL_LIMIT_EVENT, show);
  }, []);
  return (
    <>
    {!userData && (
      <aside className={`free-credit-counter${creditsExpanded ? "" : " is-collapsed"}`} style={{ top: navbarBottom }} aria-label="Free tool credits" onKeyDown={(event) => {
        if (event.key === "Escape") {
          setCreditsExpanded(false);
          event.currentTarget.querySelector("button")?.focus();
        }
      }}>
        {flyingCredits.map(id => (
          <span key={id} className="free-credit-flying-star" aria-hidden="true" onAnimationEnd={() => setFlyingCredits(credits => credits.filter(credit => credit !== id))}>
            <FiStar fill="currentColor" /><span>−1</span>
          </span>
        ))}
        <button type="button" className="free-credit-toggle" aria-expanded={creditsExpanded} aria-controls="free-credit-details" onClick={() => setCreditsExpanded(value => !value)}>
          <FiStar aria-hidden="true" fill="currentColor" />
          <span>Your free credits</span>
          <span className="free-credit-toggle-count" aria-live="polite" aria-atomic="true">{Math.min(used, FREE_TOOL_LIMIT)}/{FREE_TOOL_LIMIT} used</span>
          {creditsExpanded && <FiX aria-hidden="true" />}
        </button>
        <div id="free-credit-details" hidden={!creditsExpanded}>
        <div className="free-credit-heading"><span className="free-credit-icon"><FiStar aria-hidden="true" fill="currentColor" /></span><span>YOUR SEO BOOST</span><span className="free-credit-badge">FREE</span></div>
        <div role="status" aria-live="polite" aria-atomic="true">
          <strong>Credits used <span>{Math.min(used, FREE_TOOL_LIMIT)}/{FREE_TOOL_LIMIT}</span></strong>
          <p>{used > 0 ? `${Math.min(used, FREE_TOOL_LIMIT)} used · ${remaining} remaining` : "5 free credits · No login needed"}</p>
        </div>
        <div className="free-credit-meter" aria-hidden="true">
          {Array.from({ length: FREE_TOOL_LIMIT }, (_, index) => <span key={index} className={index < used ? "available" : ""} />)}
        </div>
        {remaining > 0 ? <small>Each tool request uses 1 credit</small> : <a href="https://dashboard.seobix.com/">Log in to continue <FiArrowRight aria-hidden="true" /></a>}
        </div>
      </aside>
    )}
    <Modal show={open} onHide={() => setOpen(false)} centered className="tool-limit-modal" aria-labelledby="tool-limit-title" aria-describedby="tool-limit-description">
      <Modal.Body className="tool-limit-body">
        <button type="button" className="tool-limit-close" onClick={() => setOpen(false)} aria-label="Close"><FiX aria-hidden="true" /></button>
        <div className="tool-limit-hero">
          <span className="tool-limit-eyebrow">YOUR NEXT STEP WITH SEOBIX</span>
          <div className="tool-limit-symbol"><FiTrendingUp aria-hidden="true" /><span><FiLock aria-hidden="true" /></span></div>
          <h2 id="tool-limit-title">Your free searches<br />are <span>used up.</span></h2>
          <p id="tool-limit-description">You’ve explored your {FREE_TOOL_LIMIT} free tool requests.<br />Keep discovering opportunities to grow.</p>
        </div>
        <div className="tool-limit-usage">
          <div><span>Free tool requests</span><strong>{FREE_TOOL_LIMIT} of {FREE_TOOL_LIMIT} used</strong></div>
          <div className="tool-limit-meter" aria-hidden="true">{Array.from({ length: FREE_TOOL_LIMIT }, (_, index) => <span key={index} />)}</div>
          <p><FiCheck aria-hidden="true" /> Free allowance completed</p>
        </div>
        <div className="tool-limit-actions">
          <p>Log in to your SeoBix dashboard or choose<br className="tool-limit-desktop-break" /> a plan to continue using the tools.</p>
          <a className="tool-limit-primary" href="https://dashboard.seobix.com/">Log in to continue <FiArrowRight aria-hidden="true" /></a>
          <a className="tool-limit-secondary" href="/pricing-plans">Explore plans <FiArrowRight aria-hidden="true" /></a>
          <button type="button" className="tool-limit-later" onClick={() => setOpen(false)}>Maybe later</button>
        </div>
      </Modal.Body>
    </Modal>
    </>
  );
}
